import React from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const DownloadOptions = ({ resultsRef, faceShape, imageUrl, recommendations }) => {
  // Function to download as JPEG
  const downloadAsJPEG = async () => {
    if (!resultsRef.current) return;
    
    try {
      // Create canvas from the results element
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2, // Higher scale for better quality
        logging: false,
        useCORS: true, // To allow loading cross-origin images
        backgroundColor: '#ffffff'
      });
      
      // Convert canvas to data URL
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      
      // Create download link
      const link = document.createElement('a');
      link.href = imageData;
      link.download = `face-analysis-${faceShape}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading as JPEG:', error);
      alert('Error creating image. Please try again.');
    }
  };
  
  // Function to download as PDF
  const downloadAsPDF = async () => {
    if (!resultsRef.current) return;
    
    try {
      // Create canvas from the results element
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      
      // Initialize PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // PDF dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate aspect ratio to fit the image within the PDF
      const canvasAspectRatio = canvas.width / canvas.height;
      let imgWidth = pdfWidth - 20; // 10mm margin on each side
      let imgHeight = imgWidth / canvasAspectRatio;
      
      // If image is too tall, scale it to fit the page height
      if (imgHeight > pdfHeight - 30) { // 15mm margin top and bottom
        imgHeight = pdfHeight - 30;
        imgWidth = imgHeight * canvasAspectRatio;
      }
      
      // Add title to PDF
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Face Shape Analysis Results', pdfWidth / 2, 15, { align: 'center' });
      
      // Add image to PDF
      pdf.addImage(
        imageData, 
        'JPEG', 
        (pdfWidth - imgWidth) / 2, // Center image horizontally
        20, // Position after title
        imgWidth, 
        imgHeight
      );
      
      // Add footer with date
      const dateStr = new Date().toLocaleDateString();
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on ${dateStr} by Face Detector AI`, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
      
      // Save PDF
      pdf.save(`face-analysis-${faceShape}.pdf`);
    } catch (error) {
      console.error('Error downloading as PDF:', error);
      alert('Error creating PDF. Please try again.');
    }
  };
  
  return (
    <div className="mt-8 bg-white p-4 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-3">Download Results</h3>
      
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <button 
          onClick={downloadAsJPEG}
          className="btn-primary flex items-center justify-center"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Download as JPEG
        </button>
        
        <button 
          onClick={downloadAsPDF}
          className="btn-secondary flex items-center justify-center"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Download as PDF
        </button>
      </div>
      
      <p className="mt-3 text-sm text-center text-gray-500">
        Download your results to keep them for reference or share with others.
      </p>
    </div>
  );
};

export default DownloadOptions;